import React, { useCallback, useEffect, useState } from "react";
import { CursorClick01 } from "@untitledui/icons/CursorClick01";
import { ActionButton, Floating } from "@talentbait/shared-components";
import {
  DescriptionContainer,
  DescriptionTextarea,
  editStyles,
  MetaInfo,
  MetaInfoItem,
  TextDesc,
  Title,
  FloatingContainer
} from "./ProfileSummary.styled";
import {
  addReqsButtonStyles,
  deleteButtonStyles,
  FloatingHeader,
  PageHeader,
  Skeleton
} from "../Profile.styled";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { withRefluxStore } from "../../../../Stores";
import ProfilesStore from "../../Profile.store";
import Actions from "../../../../Actions";
import logger from "../../../../utils/LoggerFrontend";
import { Group } from "../../../../styles/layout";

const ProfileSummary = ({
  isLoaded,
  // Store
  userProfile,
  profile
}) => {
  const { t } = useTranslation();

  const userIsAdmin = userProfile?.superRank === "admin";
  const [openMetaInfo, setOpenMetaInfo] = useState(false);

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [localDescription, setLocalDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (profile.description) {
      setLocalDescription(profile.description);
    }
  }, [profile.description]);

  const closeEditing = useCallback(() => {
    setIsEditingDescription(false);
    setLocalDescription(profile.description);
  }, [profile.description]);

  const handleEditing = useCallback((e) => setLocalDescription(e.target.value), []);

  const handleSaveEdit = useCallback(() => {
    const ref = Actions.patchProfile.completed.listen(() => {
      setIsEditingDescription(false);
      setIsLoading(false);

      Actions.addNotification(t("Description updated"));
    });

    const refFailed = Actions.patchProfile.failed.listen(() => {
      setIsEditingDescription(false);
      setIsLoading(false);
      logger.log(
        "error:ProfileDescription:handleSaveEdit",
        "failed to save profile description",
        profile
      );
    });

    Actions.patchProfile(profile.id, { description: localDescription });
    setIsLoading(true);

    return () => {
      ref();
      refFailed();
    };
  }, [localDescription, profile, t]);

  return (
    <>
      <Floating
        placement="bottom"
        open={openMetaInfo}
        onOpenChange={setOpenMetaInfo}
        enabled={userIsAdmin}
        trigger={
          <Title $hasPermission={userIsAdmin} onClick={() => setOpenMetaInfo(true)}>
            {isLoaded ? profile.title : <Skeleton margin="0px 0px 46px" />}
            {userIsAdmin && isLoaded && <CursorClick01 size={14} />}
          </Title>
        }
        content={
          <FloatingContainer>
            <FloatingHeader>{t("Admin View")}</FloatingHeader>
            <MetaInfoItem>
              <p>ID</p>
              <p>{profile.id}</p>
            </MetaInfoItem>
            <MetaInfoItem>
              <p>Job Category</p>
              <p>{profile.jobCategoryId}</p>
            </MetaInfoItem>
            <MetaInfoItem>
              <p>Created Date</p>
              <p>{new Date(profile.createdDate).toLocaleDateString()}</p>
            </MetaInfoItem>
            <MetaInfoItem>
              <p>Updated Date</p>
              <p>{new Date(profile.updatedDate).toLocaleDateString()}</p>
            </MetaInfoItem>
            <MetaInfoItem>
              <p>Created By</p>
              <p>
                {profile.createdBy || "N/A"}
                {profile.userId && <span>userId: {profile.userId}</span>}
              </p>
            </MetaInfoItem>
          </FloatingContainer>
        }
      />

      <MetaInfo>
        <MetaInfoItem>
          <p>{t("Team")}</p>
          {isLoaded ? <p>{profile.team || "N/A"}</p> : <Skeleton />}
        </MetaInfoItem>
        <MetaInfoItem>
          <p>{t("Experience")}</p>
          {isLoaded ? <p>{profile.experience || "N/A"}</p> : <Skeleton />}
        </MetaInfoItem>
        <MetaInfoItem>
          <p>{t("Level")}</p>
          {isLoaded ? <p>{profile.level || "N/A"}</p> : <Skeleton />}
        </MetaInfoItem>
        <MetaInfoItem>
          <p>{t("Contract")}</p>
          {isLoaded ? <p>{profile.contract || "N/A"}</p> : <Skeleton />}
        </MetaInfoItem>
      </MetaInfo>

      <DescriptionContainer $isEmpty={!localDescription}>
        <PageHeader>{t("Description")}</PageHeader>

        {isLoaded ? (
          isEditingDescription ? (
            <>
              <DescriptionTextarea
                id="profile-description"
                value={localDescription || ""}
                onChange={handleEditing}
                placeholder={t("Profile description...")}
                minRows={2}
              />
              <Group direction="row" align="center">
                <ActionButton
                  variant="create"
                  size="small"
                  onClick={handleSaveEdit}
                  text={t("Save")}
                  styles={addReqsButtonStyles}
                  loading={isLoading}
                />
                <ActionButton
                  variant="filter"
                  onClick={closeEditing}
                  text={t("Cancel")}
                  styles={deleteButtonStyles}
                  useSmall
                />
              </Group>
            </>
          ) : (
            <>
              <TextDesc $isEmpty={!localDescription}>
                {localDescription || t("Click to add profile description...")}
              </TextDesc>
              <ActionButton
                id="edit-filter-button"
                variant="filter"
                onClick={() => setIsEditingDescription(true)}
                text={t("Edit")}
                styles={editStyles}
                useSmall
              />
            </>
          )
        ) : (
          <Skeleton height="44px" />
        )}
      </DescriptionContainer>
    </>
  );
};

export default withRouter(
  withRefluxStore(ProfileSummary, [ProfilesStore], ["userProfile", "profile"])
);
